//-- Copyright (c) 2021 TMT Insights Group, LLC
import LabelValue from '@/components/_common/LabelValue';
import Modal from '@/components/_common/Modal/Modal';
import Spinner from '@/components/_common/Spinner';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useToast } from '@/components/_common/Toast';
import { IngestFile } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Box, Button, Grid, Paper } from '@mui/material';
import { RJSFSchema } from '@rjsf/utils';
import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import axios from '@/_utils/axios';
import { insertWordBreaks, formatBytes } from '@/_utils/utils';
import FileTypeDisplay from '@/components/_common/FileTypeDisplay';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import DateTimeDisplay from '@/components/_common/DateTimeDisplay';

// data.ingest_schema gives us the schema for the fields needed in order to request the schema to register the ingest file.

type IngestRegisterModalProps = {
  open: boolean;
  toggleOpen: () => void;
  data: IngestFile | Record<string, unknown>;
};
const IngestRegisterModal = ({ open, toggleOpen, data = {} }: IngestRegisterModalProps) => {
  const { success, error } = useToast();
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('none');

  // top form to request our main registration form
  const instanceSchemaFormRef = useRef(null);
  const [ingestSchemaInput, setIngestSchemaInput] = useState<RJSFSchema>({});

  // our main registration form
  const schemaRef = useRef<any>(null);
  const [versionSchema, setVersionSchema] = useState(null);

  // the form values of the schema we've retrieved
  const [schemaInput, setSchemaInput] = useState((data as IngestFile).schema_inputs || {});

  const onSubmit = () => {
    const formData = (schemaRef.current as any).state.formData;
    const isValid = (schemaRef.current as any).validateForm();
    setSchemaInput(formData);
    if (!isValid) {
      return error('Some fields have errors.');
    } else {
      setLoading(true);
      axios
        .post(`/inventory/file_ingests/${(data as IngestFile).id}/submit/`, { ...formData, language: 'eng' })
        .then(() => {
          success('Successfully registered file.');
          toggleOpen();
          setSchemaInput({});
        })
        .catch((err) => error(err.response?.data?.message || 'Failed to register file.'))
        .finally(() => setLoading(false));
    }
  };

  const onCancel = (_: MouseEventHandler, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    toggleOpen();
  };

  useEffect(() => {
    // this is a bad way to do this but it works for now
    const ingestProperties: string[] =
      ((data as IngestFile)?.ingest_schema?.properties && Object.keys((data as any)?.ingest_schema.properties)) || [];

    // if the lengths match, we can validate the form
    if (ingestProperties.length && instanceSchemaFormRef.current) {
      const validationResult = (instanceSchemaFormRef.current as any).validate(ingestSchemaInput);
      if (validationResult && !validationResult.errors?.length) {
        setView('loading');
        axios
          .post(`/inventory/file_ingests/${(data as IngestFile).id}/form/`, ingestSchemaInput)
          .then((res) => {
            setVersionSchema(res.data?.data?.schema);
            setSchemaInput(res.data?.data?.schema_input);
            setView('schema');
          })
          .catch((err) => {
            console.error('Output Version Selector: ', err);
            setVersionSchema(null);
            setView('error');
          });
      }
    } else {
      setView('none');
    }
  }, [ingestSchemaInput]);

  const getSchemaView = () => {
    switch (view) {
      case 'loading':
        return <Spinner style={{ position: 'relative', marginTop: 50, marginBottom: 50 }} />;
      case 'schema':
        if (versionSchema) {
          return (
            <SchemaForm showFormTitle formRef={schemaRef} schema={versionSchema} formData={schemaInput}>
              <Grid container sx={{ mt: 3 }}>
                <Button color="default" onClick={onCancel as any} sx={{ ml: 'auto', mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSubmit();
                  }}
                >
                  Register
                </Button>
              </Grid>
            </SchemaForm>
          );
        }
      case 'error':
        return (
          <ErrorWrapper>
            <StyledErrorIcon icon="error_filled" height={18} width={18} />
            <span>No schema linked to this version</span>
          </ErrorWrapper>
        );
      case 'none':
        return (
          <ErrorWrapper>
            <StyledInfoIcon icon="info_filled" height={18} width={18} />
            <span>Select your asset type to view registration form</span>
          </ErrorWrapper>
        );
      default:
        return <></>;
    }
  };

  return (
    <RegisterModal open={open} onClose={onCancel} title="Register File" loading={loading} icon={<PolarisIcon icon="link" height={24} />}>
      <Paper elevation={3} sx={{ p: 3, mb: 4, backgroundColor: 'background.default' }}>
        <Grid container columnSpacing={6} rowSpacing={3}>
          <LabelValue label="Filename" sx={{ width: '100%' }}>
            <span style={{ lineHeight: 1.3 }}>{insertWordBreaks((data as IngestFile).filename) || ''}</span>
          </LabelValue>
          <Grid item xs={3}>
            <LabelValue label="Type">
              <FileTypeDisplay type={(data as IngestFile).file_type} small />
            </LabelValue>
          </Grid>
          <Grid item xs={4}>
            <LabelValue label="Created at">
              <DateTimeDisplay>{(data as IngestFile).created_at}</DateTimeDisplay>
            </LabelValue>
          </Grid>
          <Grid item xs>
            <LabelValue label="File Size">{(data as IngestFile).file_size ? formatBytes((data as IngestFile).file_size) : null}</LabelValue>
          </Grid>
        </Grid>
      </Paper>
      <SchemaFormWrapper>
        <DisableFields disabled={view === 'loading'}>
          <SchemaForm
            formRef={instanceSchemaFormRef}
            schema={(data as IngestFile).ingest_schema}
            formData={ingestSchemaInput}
            onChange={(data) => setIngestSchemaInput(data.formData)}
          />
        </DisableFields>
        <VersionSchemaWrapper>{getSchemaView()}</VersionSchemaWrapper>
      </SchemaFormWrapper>
    </RegisterModal>
  );
};

export default IngestRegisterModal;

const RegisterModal = styled(Modal)`
  .MuiPaper-root > .MuiBox-root {
    max-height: none;
    position: relative;
  }

  .modal-body {
    overflow: unset;
  }
`;

const SchemaFormWrapper = styled(Box)`
  max-height: 64vh;
  overflow: auto;
  margin: 0 ${(props) => props.theme.spacing(-4)};
  padding: 0 ${(props) => props.theme.spacing(4)};
  margin-top: ${(props) => props.theme.spacing(3)};
`;

const VersionSchemaWrapper = styled.div`
  padding-top: 25px;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 10px;
  margin: 0 auto;
  width: 100%;
  justify-content: center;
  align-items: center;
  height: 100px;
`;

const StyledErrorIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.error.main};
  height: 18px;
`;

const StyledInfoIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.info.main};
  height: 18px;
`;

const DisableFields = styled.div<{ disabled: boolean }>`
  .MuiInputBase-root {
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  }

  .MuiFormLabel-root {
    opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  }

  .MuiGrid-root {
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  }
`;
